import { useMemo, useState } from "react"
import { Badge, Box, Code, HStack, Spinner, Table, Text, VStack } from "@chakra-ui/react"
import { FiChevronDown, FiChevronRight } from "react-icons/fi"
import { useAlertTemplateAdmin, type GlobalAlertTemplate } from "../../hooks/useAlertTemplateAdmin"

function buildFullConfig(template: GlobalAlertTemplate) {
  return {
    id: template.id,
    name: template.name,
    severity: template.severity,
    evaluationTier: template.evaluationTier,
    scopeLevel: template.scopeLevel,
    overridable: template.overridable,
    enabled: template.enabled,
    ...(template.metric && { metric: template.metric }),
    ...(template.conditionType && { conditionType: template.conditionType }),
    ...(template.operator && { operator: template.operator }),
    ...(template.threshold != null && { threshold: template.threshold }),
    ...(template.thresholdUnit && { thresholdUnit: template.thresholdUnit }),
    ...(template.thresholdDirection && { thresholdDirection: template.thresholdDirection }),
    ...(template.timeWindowSeconds != null && { timeWindowSeconds: template.timeWindowSeconds }),
    ...(template.compositeConfig && { compositeConfig: template.compositeConfig }),
  }
}

function AlertExpandedRow({ template }: { template: GlobalAlertTemplate }) {
  const configJson = useMemo(
    () => JSON.stringify(buildFullConfig(template), null, 2),
    [template]
  )

  return (
    <Box p={4} bg="bg.subtle" borderTopWidth="1px">
      <HStack gap={6} align="start" flexWrap="wrap">
        {/* Configuration */}
        <VStack gap={2} align="stretch" flex={1} minW="300px">
          <Text fontSize="sm" fontWeight="medium">Configuration</Text>
          <Box
            bg="bg.panel"
            borderWidth="1px"
            borderRadius="md"
            p={3}
            overflowX="auto"
            maxH="280px"
            overflowY="auto"
          >
            <Code fontSize="xs" whiteSpace="pre" display="block">
              {configJson}
            </Code>
          </Box>
        </VStack>

        {/* Assignments */}
        <VStack gap={2} align="stretch" flex={1} minW="300px">
          <Text fontSize="sm" fontWeight="medium">
            Assignments ({template.assignments?.length ?? 0})
          </Text>
          {!template.assignments?.length ? (
            <Text fontSize="sm" color="fg.muted">No assignments yet.</Text>
          ) : (
            <Box overflowX="auto">
              <Table.Root size="sm" variant="outline">
                <Table.Header>
                  <Table.Row>
                    <Table.ColumnHeader>Organisation</Table.ColumnHeader>
                    <Table.ColumnHeader>Target</Table.ColumnHeader>
                    <Table.ColumnHeader>Enabled</Table.ColumnHeader>
                    <Table.ColumnHeader>Override</Table.ColumnHeader>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {(template.assignments ?? []).map(a => {
                    const hasOverride = a.thresholdOverride != null || a.timeWindowOverrideSeconds != null
                    return (
                      <Table.Row key={a.id}>
                        <Table.Cell>
                          <Text fontSize="xs">{a.tenantName || a.tenantId}</Text>
                        </Table.Cell>
                        <Table.Cell>
                          <Text fontSize="xs" color="fg.muted">
                            {a.systemId ? `System` : a.assetId ? `Asset` : "—"}
                          </Text>
                        </Table.Cell>
                        <Table.Cell>
                          <Badge size="sm" colorPalette={a.enabled ? "green" : "gray"}>
                            {a.enabled ? "Yes" : "No"}
                          </Badge>
                        </Table.Cell>
                        <Table.Cell>
                          {hasOverride ? (
                            <VStack gap={0} align="start">
                              {a.thresholdOverride != null && (
                                <Text fontSize="xs">
                                  Threshold: {a.thresholdOverride}{template.thresholdUnit ?? ""}
                                </Text>
                              )}
                              {a.timeWindowOverrideSeconds != null && (
                                <Text fontSize="xs">
                                  Window: {a.timeWindowOverrideSeconds}s
                                </Text>
                              )}
                            </VStack>
                          ) : (
                            <Text fontSize="xs" color="fg.muted">Default</Text>
                          )}
                        </Table.Cell>
                      </Table.Row>
                    )
                  })}
                </Table.Body>
              </Table.Root>
            </Box>
          )}
        </VStack>
      </HStack>
    </Box>
  )
}

export function AlertTemplateManager() {
  const { templates, loading, error } = useAlertTemplateAdmin()
  const [expandedId, setExpandedId] = useState<string | null>(null)

  if (loading && !templates.length) {
    return (
      <Box p={4} display="flex" justifyContent="center">
        <Spinner />
      </Box>
    )
  }

  if (error) {
    return (
      <Box p={4}>
        <Text color="red.500">Failed to load alerts: {error.message}</Text>
      </Box>
    )
  }

  return (
    <VStack gap={4} align="stretch">
      <HStack justify="space-between">
        <Text fontSize="lg" fontWeight="semibold">Alerts</Text>
      </HStack>

      {templates.length === 0 ? (
        <Text color="fg.muted">No alerts configured yet.</Text>
      ) : (
        <Box overflowX="auto">
          <Table.Root size="sm" variant="outline">
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeader w="24px" />
                <Table.ColumnHeader>Name</Table.ColumnHeader>
                <Table.ColumnHeader>Severity</Table.ColumnHeader>
                <Table.ColumnHeader>Scope</Table.ColumnHeader>
                <Table.ColumnHeader>Organisations</Table.ColumnHeader>
                <Table.ColumnHeader>Assignments</Table.ColumnHeader>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {templates.map(template => {
                const isExpanded = expandedId === template.id
                return (
                  <>
                    <Table.Row
                      key={template.id}
                      cursor="pointer"
                      _hover={{ bg: "bg.subtle" }}
                      onClick={() => setExpandedId(isExpanded ? null : template.id)}
                    >
                      <Table.Cell>
                        {isExpanded ? <FiChevronDown size={14} /> : <FiChevronRight size={14} />}
                      </Table.Cell>
                      <Table.Cell>
                        <VStack gap={0} align="start">
                          <Text fontSize="sm" fontWeight="medium">{template.name}</Text>
                          {template.description && (
                            <Text fontSize="xs" color="fg.muted" lineClamp={1}>{template.description}</Text>
                          )}
                        </VStack>
                      </Table.Cell>
                      <Table.Cell>
                        <Badge size="sm" colorPalette={
                          template.severity === "critical" ? "red" :
                          template.severity === "warning" ? "orange" : "blue"
                        }>
                          {template.severity}
                        </Badge>
                      </Table.Cell>
                      <Table.Cell>
                        <Text fontSize="sm">{template.scopeLevel ?? "—"}</Text>
                      </Table.Cell>
                      <Table.Cell>
                        <Text fontSize="sm">{template.visibleToTenants.length}</Text>
                      </Table.Cell>
                      <Table.Cell>
                        <Text fontSize="sm">{template.assignmentCount}</Text>
                      </Table.Cell>
                    </Table.Row>
                    {isExpanded && (
                      <Table.Row key={`${template.id}-expanded`}>
                        <Table.Cell colSpan={6} p={0}>
                          <AlertExpandedRow template={template} />
                        </Table.Cell>
                      </Table.Row>
                    )}
                  </>
                )
              })}
            </Table.Body>
          </Table.Root>
        </Box>
      )}
    </VStack>
  )
}
